import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client';

import { diskStore } from '@/shared/services/storage/disk-store';

import { useSessionStore } from '@/stores/session.store';

/**
 * Persistance du cache React Query sur le disque de l'appareil.
 *
 * C'est la piece maitresse de l'application hors connexion : tout ce qui a été
 * téléchargé une fois (fil, catalogue, commandes, discussions) se rouvre tel
 * quel au prochain démarrage, sans un octet de réseau. La connexion ne sert
 * plus qu'a rafraichir, jamais a réafficher.
 *
 * A bumper quand la **forme** des réponses de l'API change de facon
 * incompatible : un cache écrit sous l'ancienne forme serait alors rendu a des
 * écrans qui ne savent plus la lire. La bibliotheque jette d'elle-meme tout
 * cache portant une autre version.
 */
// v2 : les stats plateforme gagnent les compteurs de files, la boutique son
// `activitySectorId` : un cache v1 rendu tel quel laisserait pastilles et
// select du secteur sans données jusqu'au prochain passage réseau.
//
// v3 : retours du terrain. Le tableau de bord porte le type des visiteurs, la
// commande son canal (en ligne, comptoir), le commentaire son auteur boutique
// et sa date de modification, l'annonce la période de sa promotion, le message
// la publication partagée. Un cache v2 rendrait ces écrans sans ces champs.
//
// v4 : réunion des retours du terrain et du fil recommandé, du direct et des
// réactions. Les annonces et les publications de communauté portent aussi le
// détail de leurs réactions (`reactionCounts`). Chacune des deux branches avait
// posé sa propre v3 : un appareil passé par l'une n'aurait pas les champs de
// l'autre, d'où une version neuve.
export const PERSIST_VERSION = 'v4';

/**
 * Sept jours, en mémoire (`gcTime`) comme sur disque (`maxAge` du persister) :
 * les deux durées doivent rester égales, car une entrée sortie de la mémoire
 * disparait du disque a l'écriture suivante.
 */
export const CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const FILE_NAME = 'query-cache.json';

/**
 * Le cache est réécrit en entier a chaque changement : on regroupe les rafales
 * (une invalidation en cascade, un écran qui charge trois listes) en une seule
 * écriture. Deux secondes de retard au pire : ce qui arrive apres sera dans la
 * prochaine écriture, et un cache est par nature en retard sur le serveur.
 */
const THROTTLE_MS = 2_000;

/**
 * Bornes du fichier. Sans elles, sept jours de navigation (chaque combinaison
 * de filtres est une entrée, chaque page défilée s'ajoute a la sienne) feraient
 * un JSON de plusieurs mégaoctets a sérialiser a chaque écriture. On garde les
 * entrées les plus récentes, et les premieres pages seulement : c'est ce qu'un
 * écran montre a l'ouverture, la suite se recharge en défilant.
 */
const MAX_PERSISTED_QUERIES = 150;
const MAX_PERSISTED_PAGES = 3;

type PersistedQuery = PersistedClient['clientState']['queries'][number];

/** Enveloppe écrite sur disque : le cache, et le compte auquel il appartient. */
type Envelope = { account: string; client: PersistedClient };

/**
 * Le cache est nominatif. L'estampille du compte double la garde de
 * `SessionGuard` (qui vide la mémoire au changement de compte) pour le chemin
 * que la mémoire ne couvre pas : l'application tuée avant que la derniere
 * écriture ne parte, puis rouverte par quelqu'un d'autre.
 */
function accountId(): string {
  return useSessionStore.getState().me?.id ?? 'anon';
}

function trimPages(query: PersistedQuery): PersistedQuery {
  const key = query.queryKey;
  const data = query.state.data;
  // Les listes a défilement infini se reconnaissent a leur suffixe (posé par
  // `usePaginatedQuery`) et a leur forme `{ pages, pageParams }`.
  if (key[key.length - 1] !== 'infinite' || data === null || typeof data !== 'object') {
    return query;
  }
  const paged = data as { pages?: Array<unknown>; pageParams?: Array<unknown> };
  if (!Array.isArray(paged.pages) || paged.pages.length <= MAX_PERSISTED_PAGES) {
    return query;
  }
  return {
    ...query,
    state: {
      ...query.state,
      data: {
        pages: paged.pages.slice(0, MAX_PERSISTED_PAGES),
        pageParams: (paged.pageParams ?? []).slice(0, MAX_PERSISTED_PAGES),
      },
    },
  };
}

/** Ce qui part sur le disque : le plus récent d'abord, borné, pages rognées. */
function prune(client: PersistedClient): PersistedClient {
  const now = Date.now();
  const queries = client.clientState.queries
    .filter((query) => now - query.state.dataUpdatedAt <= CACHE_MAX_AGE_MS)
    .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)
    .slice(0, MAX_PERSISTED_QUERIES)
    .map(trimPages);
  return { ...client, clientState: { ...client.clientState, queries } };
}

export function createCachePersister(): Persister {
  let queued: PersistedClient | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const flush = () => {
    timer = null;
    const client = queued;
    queued = null;
    if (!client) {
      return;
    }
    try {
      diskStore.write(FILE_NAME, JSON.stringify({ account: accountId(), client: prune(client) }));
    } catch {
      // Un cache qui ne s'écrit pas n'est pas une panne : l'application vit
      // exactement comme avant lui, elle rechargera simplement du réseau.
    }
  };

  return {
    persistClient(client) {
      queued = client;
      timer ??= setTimeout(flush, THROTTLE_MS);
    },

    async restoreClient() {
      const raw = await diskStore.read(FILE_NAME);
      if (!raw) {
        return undefined;
      }
      try {
        const envelope = JSON.parse(raw) as Partial<Envelope>;
        if (!envelope.client || envelope.account !== accountId()) {
          diskStore.remove(FILE_NAME);
          return undefined;
        }
        return envelope.client;
      } catch {
        // Fichier tronqué (application tuée en pleine écriture) : on repart
        // d'un cache vide plutot que de faire échouer le démarrage.
        diskStore.remove(FILE_NAME);
        return undefined;
      }
    },

    removeClient() {
      queued = null;
      diskStore.remove(FILE_NAME);
    },
  };
}
